import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import LeadsTable from './components/LeadsTable/LeadsTable';
import LeadsSearchActions from './components/LeadsSearchActions/LeadsSearchActions';
import LeadsPagination from './components/LeadsPagination/LeadsPagination';
import AddLeadModal from './components/AddLeadModal/AddLeadModal';
import LeadsFilterDropdown, { LeadsControls } from './components/LeadsFilterDropdown/LeadsFilterDropdown';
import { saveLead, deleteLeads, updateLeadStatus, SavedLead, fetchLeads, updateLead } from '../../services/leadsService';
import { startBulkSend, validateLeadsForBulkSend } from '../../services/bulkSendService';
import { fetchAgentConfig } from '../../services/aiConfigService';
import { verifyPhoneNumbers, VerificationResult } from '../../services/whatsappVerificationService';
import { normalizePhoneNumber, hasValidDialCode } from '../../utils/ddiUtils'; // Import DDI utilities
import { fetchCredential } from '../../services/credentialsService';
import { useBulkSendConfig } from '../../hooks/useBulkSendConfig';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal';

interface Lead extends SavedLead { }

const RESULTS_PER_PAGE = 10;

const LeadsSalvos: React.FC = () => {
  const { t } = useTranslation();
  const { intervalConfig } = useBulkSendConfig();

  const [allLeads, setAllLeads] = useState<Lead[]>([]);
  const [selectedLeads, setSelectedLeads] = useState<string[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isWhatsappConnected, setIsWhatsappConnected] = useState(false);

  const defaultControls: LeadsControls = {
    rating: 0,
    whatsappStatus: 'all',
    sortBy: 'date_desc',
  };
  const [controls, setControls] = useState<LeadsControls>(defaultControls);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);

  const [isProspectedWarningModalOpen, setIsProspectedWarningModalOpen] = useState(false);
  const [prospectedLeadsCount, setProspectedLeadsCount] = useState(0);
  const [isValidationWarningModalOpen, setIsValidationWarningModalOpen] = useState(false);
  const [validationInfo, setValidationInfo] = useState({ pendingCount: 0, invalidCount: 0, validCount: 0 });

  useEffect(() => {
    const loadData = async () => {
      try {
        const [fetchedLeads, whatsappCreds] = await Promise.all([
          fetchLeads(),
          fetchCredential('whatsapp'),
        ]);

        setAllLeads(fetchedLeads);
        setIsWhatsappConnected(!!whatsappCreds && !!whatsappCreds.api_key && whatsappCreds.is_active);

      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : t('Erro ao carregar leads.');
        toast.error(errorMessage);
        setAllLeads([]);
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, [t]);

  const filteredAndSortedLeads = useMemo(() => {
    let filtered = allLeads;

    if (controls.whatsappStatus !== 'all') {
      filtered = filtered.filter(lead => {
        if (controls.whatsappStatus === 'validated' && lead.whatsapp_status !== 'Validado') return false;
        if (controls.whatsappStatus === 'pending' && lead.whatsapp_status !== 'Pendente') return false;
        if (controls.whatsappStatus === 'invalid' && lead.whatsapp_status !== 'Inválido') return false;
        return true;
      });
    }

    if (controls.rating > 0) {
      filtered = filtered.filter(lead => lead.rating >= controls.rating);
    }

    filtered.sort((a, b) => {
      switch (controls.sortBy) {
        case 'date_desc':
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        case 'date_asc':
          return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
        case 'name_asc':
          return a.company_name.localeCompare(b.company_name);
        case 'name_desc':
          return b.company_name.localeCompare(a.company_name);
        case 'rating_desc':
          return b.rating - a.rating;
        case 'rating_asc':
          return a.rating - b.rating;
        default:
          return 0;
      }
    });

    setCurrentPage(1);
    return filtered;
  }, [allLeads, controls]);

  const totalPages = Math.ceil(filteredAndSortedLeads.length / RESULTS_PER_PAGE);
  const startIndex = (currentPage - 1) * RESULTS_PER_PAGE;
  const displayedLeads = filteredAndSortedLeads.slice(startIndex, startIndex + RESULTS_PER_PAGE);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleClearFilters = () => {
    setControls(defaultControls);
  };

  const handleAddLead = () => {
    setIsModalOpen(true);
  };

  // Helper function to normalize phone numbers for storage
  const normalizePhoneForStorage = (phoneNumber: string, countryCode: string): string | undefined => {
    if (!phoneNumber) return undefined;

    const cleanedPhone = phoneNumber.replace(/[\s\-()]/g, '');

    // If it already has a valid DDI, just return it (remove leading + for storage if present)
    if (hasValidDialCode(cleanedPhone)) {
      return cleanedPhone.replace(/^\+/, '');
    }

    // Otherwise, try to normalize using the provided country code
    const normalized = normalizePhoneNumber(cleanedPhone, countryCode);
    if (normalized) {
      return normalized.replace(/^\+/, '');
    }

    // If normalization fails, return the cleaned phone number as a fallback
    return cleanedPhone.replace(/^\+/, ''); // Ensure no leading + if it somehow got there without being a valid DDI
  };

  const handleSaveNewLead = async (formData: any) => {
    try {
      // Normalize phone number with DDI based on selected country
      let normalizedPhone = formData.phone || undefined;
      if (formData.phone && formData.country) {
        const normalized = normalizePhoneForStorage(formData.phone, formData.country);
        if (normalized) {
          normalizedPhone = normalized;
        }
      }

      // Determine initial WhatsApp status
      let whatsappStatus: 'Validado' | 'Pendente' | 'Inválido' = 'Pendente';

      // If user marked "Has WhatsApp", verify it before saving
      if (formData.hasWhatsapp && normalizedPhone && formData.country) {
        const verifyToastId = toast.loading(t('verify_whatsapp_loading'));

        try {
          const verificationResults = await verifyPhoneNumbers([normalizedPhone]);

          const hasWhatsapp = verificationResults[normalizedPhone];

          if (hasWhatsapp === true) {
            whatsappStatus = 'Validado';
            toast.success(t('verify_whatsapp_found'), { id: verifyToastId });
          } else if (hasWhatsapp === false) {
            whatsappStatus = 'Inválido';
            toast.error(t('verify_whatsapp_not_found'), { id: verifyToastId });
          } else {
            whatsappStatus = 'Pendente';
            toast.dismiss(verifyToastId);
          }
        } catch {
          toast.error(t('verify_whatsapp_failed'), { id: verifyToastId });
          whatsappStatus = 'Pendente';
        }
      } else if (!formData.hasWhatsapp) {
        whatsappStatus = 'Inválido';
      }

      const leadData: any = {
        company_name: formData.companyName,
        phone_number: normalizedPhone,
        email: formData.email || undefined,
        website: formData.website || undefined,
        address: formData.address || undefined,
        category: formData.category || undefined,
        location: formData.country || undefined,
        rating: formData.rating || 0,
        whatsapp_status: whatsappStatus,
        prospected: formData.prospected || false,
      };

      const savedLead = await saveLead(leadData);

      // Atualizar a lista local
      setAllLeads(prev => [savedLead, ...prev]);

      toast.success(t('lead_saved_with_status', {
        name: formData.companyName,
        status: whatsappStatus
      }));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : t('Falha ao salvar o lead.');
      toast.error(errorMessage);
    }
  };

  const handleSearch = (_query: string) => {
    // Busca local tratada pelo filtro useMemo
  };

  const handleDeleteLeads = async (idsToDelete: string[]) => {
    try {
      await deleteLeads(idsToDelete);
      setAllLeads(prev => prev.filter(lead => !idsToDelete.includes(lead.id)));
      setSelectedLeads([]);
      toast.success(t('{{count}} lead(s) excluído(s) com sucesso.', { count: idsToDelete.length }));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : t('Erro ao excluir leads.');
      toast.error(errorMessage);
    }
  };

  const handleDeleteIndividualLead = (id: string) => {
    handleDeleteLeads([id]);
  };

  const handleLeadUpdated = (updatedLead: SavedLead) => {
    setAllLeads(prevLeads => prevLeads.map(lead =>
      lead.id === updatedLead.id ? updatedLead : lead
    ));
  };

  const handleVerifySelectedNumbers = async () => {
    if (!isWhatsappConnected) {
      toast.error(t('Por favor, conecte sua instância do WhatsApp na aba "Integrações" para usar o verificador.'));
      return;
    }

    if (selectedLeads.length === 0) {
      toast.error(t('Selecione ao menos um lead para verificar o WhatsApp.'));
      return;
    }

    setIsVerifying(true);

    const leadsToVerify = allLeads
      .filter(lead => selectedLeads.includes(lead.id))
      .filter(lead => lead.phone_number && lead.whatsapp_status !== 'Validado');

    if (leadsToVerify.length === 0) {
      toast(t('Nenhum lead selecionado precisa de verificação (ou não possui telefone).'), { icon: '✅' });
      setIsVerifying(false);
      return;
    }

    // Verificar se todos os leads têm o país configurado
    const leadsWithoutCountry = leadsToVerify.filter(lead => !lead.location);

    if (leadsWithoutCountry.length > 0) {
      toast.error(
        `${leadsWithoutCountry.length} lead(s) sem país configurado não podem ser verificados. Por favor, edite os leads para definir o país primeiro.`,
        { duration: 6000 }
      );

      setIsVerifying(false);
      return;
    }

    const phoneNumbers = leadsToVerify.map(lead => lead.phone_number!).filter(Boolean) as string[];

    // Normalize phone numbers with DDI
    // For saved leads, prioritize their saved location field, then try auto-detection
    // AND create a mapping from normalized number to lead ID
    const phoneToLeadIdMap = new Map<string, string>();

    const normalizedPhoneNumbers = phoneNumbers.map((phone, index) => {
      const lead = leadsToVerify[index];

      // First, clean the phone number (remove spaces, parentheses, dashes, etc)
      const cleanedPhone = phone.replace(/[\s\-()]/g, '');

      // If already has valid DDI (starts with +), just remove the + and return
      if (hasValidDialCode(cleanedPhone)) {
        const withoutPlus = cleanedPhone.replace(/^\+/, '');
        phoneToLeadIdMap.set(withoutPlus, lead.id);
        return withoutPlus;
      }

      // Try to use saved location first (most accurate)
      if (lead.location) {
        const normalized = normalizePhoneNumber(cleanedPhone, lead.location);
        if (normalized && normalized !== cleanedPhone) {
          const withoutPlus = normalized.replace(/^\+/, '');
          phoneToLeadIdMap.set(withoutPlus, lead.id);
          return withoutPlus;
        }
      }

      // Fallback: Try common countries in order: BR, US, FR, ES, MX
      const commonCountries = ['BR', 'US', 'FR', 'ES', 'MX'];
      for (const country of commonCountries) {
        const normalized = normalizePhoneNumber(cleanedPhone, country);
        if (normalized && normalized !== cleanedPhone) {
          const withoutPlus = normalized.replace(/^\+/, '');
          phoneToLeadIdMap.set(withoutPlus, lead.id);
          return withoutPlus;
        }
      }

      // Return original if we couldn't normalize (remove all non-digits as last resort)
      const fallback = cleanedPhone.replace(/\D/g, '');
      phoneToLeadIdMap.set(fallback, lead.id);
      return fallback;
    });

    const toastId = toast.loading(t('Verificando {{count}} número(s) de WhatsApp...', { count: normalizedPhoneNumbers.length }));

    try {
      const verificationResults: VerificationResult = await verifyPhoneNumbers(normalizedPhoneNumbers);

      let verifiedCount = 0;
      const updatePromises: Promise<SavedLead>[] = [];
      const updatedLeadsMap = new Map<string, Partial<Lead>>();

      // Use the mapping to match API results with leads
      for (const [sentNumber, leadId] of Array.from(phoneToLeadIdMap.entries())) {
        const hasWhatsapp = verificationResults[sentNumber];

        if (hasWhatsapp !== undefined) {
          const lead = allLeads.find(l => l.id === leadId);
          if (!lead) continue;

          // Prepare updates object with normalized phone number and whatsapp status
          const updates: Partial<Lead> = {};

          // Update phone number with normalized version (DDI + no symbols/spaces)
          if (lead.phone_number !== sentNumber) {
            updates.phone_number = sentNumber;
          }

          if (hasWhatsapp && lead.whatsapp_status !== 'Validado') {
            verifiedCount++;
            updates.whatsapp_status = 'Validado';
          } else if (!hasWhatsapp && lead.whatsapp_status !== 'Inválido') {
            updates.whatsapp_status = 'Inválido';
          }

          // Only update if there are changes
          if (Object.keys(updates).length > 0) {
            // Update whatsapp_status
            if (updates.whatsapp_status) {
              updatePromises.push(updateLeadStatus(lead.id, updates.whatsapp_status));
            }

            // Update phone_number if it changed
            if (updates.phone_number) {
              updatePromises.push(updateLead(lead.id, { phone_number: updates.phone_number }));
            }

            updatedLeadsMap.set(lead.id, updates);
          }
        }
      }

      await Promise.allSettled(updatePromises);

      setAllLeads(prevLeads =>
        prevLeads.map(lead => {
          const updates = updatedLeadsMap.get(lead.id);
          if (updates) {
            return { ...lead, ...updates } as Lead;
          }
          return lead;
        })
      );

      toast.success(t('Verificação concluída! {{count}} número(s) atualizado(s) para Validado.', { count: verifiedCount }), { id: toastId });

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : t('Erro ao verificar números.');
      toast.error(errorMessage, { id: toastId });
    } finally {
      setIsVerifying(false);
    }
  };

  const executeBulkSend = useCallback(async (leadsData: SavedLead[]) => {
    setIsSending(true);

    try {
      // Normalize phone numbers before sending
      const normalizedLeads = await Promise.all(leadsData.map(async (lead) => {
        if (lead.phone_number && lead.location) {
          const cleanedPhone = lead.phone_number.replace(/[\s\-()]/g, '');

          // Check if already normalized (has DDI and no symbols)
          const isNormalized = /^\d+$/.test(cleanedPhone) && hasValidDialCode(cleanedPhone);

          if (!isNormalized) {
            // Normalize the phone number
            let normalized = cleanedPhone;
            if (hasValidDialCode(cleanedPhone)) {
              normalized = cleanedPhone.replace(/^\+/, '');
            } else {
              const withDDI = normalizePhoneNumber(cleanedPhone, lead.location);
              normalized = withDDI ? withDDI.replace(/^\+/, '') : cleanedPhone.replace(/\D/g, '');
            }

            // Update in database if changed
            if (normalized !== lead.phone_number) {
              await updateLead(lead.id, { phone_number: normalized });
              return { ...lead, phone_number: normalized };
            }
          }
        }
        return lead;
      }));

      const agentConfig = await fetchAgentConfig('bulk_send');

      if (!agentConfig) {
        toast.error(t('Por favor, configure a IA de Disparo de Mensagens antes de enviar.'));
        return;
      }

      const response = await startBulkSend({
        leadsData: normalizedLeads,
        intervalConfig: intervalConfig,
        agentConfig: agentConfig,
      });

      toast.success(response.message || t('Envio em massa iniciado com sucesso!'));

      const sentIds = leadsData.map(l => l.id);
      setAllLeads(prevLeads =>
        prevLeads.map(lead =>
          sentIds.includes(lead.id) ? { ...lead, prospected: true } : lead
        )
      );

      setSelectedLeads([]);

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : t('Erro ao iniciar o envio em massa.');
      toast.error(errorMessage);
    } finally {
      setIsSending(false);
    }
  }, [intervalConfig, t]);


  const handleStartBulkSend = async () => {
    if (selectedLeads.length === 0) {
      toast.error(t('Selecione pelo menos um lead para iniciar o envio em massa.'));
      return;
    }

    const leadsData = allLeads.filter(lead => selectedLeads.includes(lead.id));
    // VALIDAÇÃO DE WHATSAPP STATUS
    const validationResult = validateLeadsForBulkSend(leadsData);
    if (validationResult.blockedCount > 0) {
      setValidationInfo({
        pendingCount: validationResult.pendingCount,
        invalidCount: validationResult.invalidCount,
        validCount: validationResult.validLeads.length,
      });
      setIsValidationWarningModalOpen(true);
      return;
    }

    const alreadyProspectedLeads = leadsData.filter(lead => lead.prospected);

    if (alreadyProspectedLeads.length > 0) {
      setProspectedLeadsCount(alreadyProspectedLeads.length);
      setIsProspectedWarningModalOpen(true);
      return;
    }

    await executeBulkSend(leadsData);
  };

  const handleConfirmProspectedSend = async (sendAll: boolean) => {
    setIsProspectedWarningModalOpen(false);

    let leadsToSend = allLeads.filter(lead => selectedLeads.includes(lead.id));

    if (!sendAll) {
      leadsToSend = leadsToSend.filter(lead => !lead.prospected);
      const newSelectedIds = leadsToSend.map(l => l.id);
      setSelectedLeads(newSelectedIds);
    }

    if (leadsToSend.length === 0) {
      toast.error(t('Nenhum lead válido para envio após a filtragem.'));
      return;
    }
    // VALIDAÇÃO DE WHATSAPP STATUS
    const validationResult = validateLeadsForBulkSend(leadsToSend);
    if (validationResult.blockedCount > 0) {
      setValidationInfo({
        pendingCount: validationResult.pendingCount,
        invalidCount: validationResult.invalidCount,
        validCount: validationResult.validLeads.length,
      });
      setIsValidationWarningModalOpen(true);
      return;
    }

    await executeBulkSend(leadsToSend);
  };

  const handleConfirmValidationSend = async () => {
    setIsValidationWarningModalOpen(false);

    const leadsToSend = allLeads.filter(lead => selectedLeads.includes(lead.id));
    const validLeads = leadsToSend.filter(lead => lead.whatsapp_status === 'Validado');

    if (validLeads.length === 0) {
      toast.error(t('Nenhum lead com número validado para envio.'));
      return;
    }

    await executeBulkSend(validLeads);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64 p-6 lg:p-10 max-w-7xl mx-auto">
        <span className="material-symbols-outlined text-4xl text-primary animate-spin">sync</span>
        <p className="ml-4 text-lg text-gray-600 dark:text-gray-400">{t('leads.loading')}</p>
      </div>
    );
  }

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="p-6 lg:p-10 max-w-7xl mx-auto"
      >
        <div className="flex flex-col gap-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <motion.h1
                className="text-4xl font-black leading-tight tracking-[-0.033em] text-transparent bg-clip-text bg-gradient-primary"
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1, duration: 0.6 }}
              >
                {t('leads.title')}
              </motion.h1>
              <motion.p
                className="mt-1 text-gray-600 dark:text-gray-400"
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2, duration: 0.6 }}
              >
                {t('leads.subtitle')}
              </motion.p>
            </div>
            <motion.button
              onClick={handleAddLead}
              className="flex h-12 w-full sm:w-auto cursor-pointer items-center justify-center overflow-hidden rounded-lg bg-gradient-primary text-white gap-2 text-sm font-bold leading-normal tracking-[0.015em] px-6 hover:opacity-90 transition-opacity"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3, duration: 0.3 }}
            >
              <span className="material-symbols-outlined">add</span>
              {t('leads.add_manual')}
            </motion.button>
          </div>

          <LeadsSearchActions
            onSearch={handleSearch}
            onFilterToggle={() => setIsFilterModalOpen(true)}
            onExport={() => { }}
            selectedLeads={selectedLeads}
            allLeads={allLeads}
            setSelectedLeads={setSelectedLeads}
            onDeleteLeads={handleDeleteLeads}
            onStartBulkSend={handleStartBulkSend}
            isSending={isSending}
            onVerifySelectedNumbers={handleVerifySelectedNumbers}
            isVerifying={isVerifying}
            isWhatsappConnected={isWhatsappConnected}
          />

          {filteredAndSortedLeads.length > 0 ? (
            <>
              <LeadsTable
                data-tour="saved-table"
                leads={displayedLeads}
                selectedLeads={selectedLeads}
                setSelectedLeads={setSelectedLeads}
                onDeleteLead={handleDeleteIndividualLead}
                onLeadUpdated={handleLeadUpdated}
              />
              <LeadsPagination
                currentPage={currentPage}
                totalResults={filteredAndSortedLeads.length}
                resultsPerPage={RESULTS_PER_PAGE}
                onPageChange={handlePageChange}
              />
            </>
          ) : (
            <div className="text-center p-6 md:p-10 border border-dashed border-gray-300 dark:border-gray-700 rounded-xl bg-gray-50 dark:bg-gray-800/50">
              <p className="text-lg text-gray-500 dark:text-gray-400">{t('leads.no_leads_title')}</p>
              <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">{t('leads.no_leads_subtitle')}</p>
            </div>
          )}
        </div>

        <AddLeadModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSave={handleSaveNewLead}
        />

        <LeadsFilterDropdown
          isOpen={isFilterModalOpen}
          onClose={() => setIsFilterModalOpen(false)}
          controls={controls}
          setControls={setControls}
          onClearFilters={handleClearFilters}
        />
      </motion.div>

      <ConfirmationModal
        isOpen={isProspectedWarningModalOpen}
        onClose={() => setIsProspectedWarningModalOpen(false)}
        onConfirm={() => handleConfirmProspectedSend(true)}
        title={t('modals.confirm_resend_title')}
        message={t('modals.confirm_resend_message', { count: prospectedLeadsCount })}
        confirmText={t('Sim, Enviar para Todos')}
        cancelText={t('Não, Enviar Apenas Novos')}
        onCancel={() => handleConfirmProspectedSend(false)}
        tipMessage={t('Dica: Se você deseja desmarcar os leads prospectados, clique em "Não, Enviar Apenas Novos" e a seleção será ajustada automaticamente.')}
      />

      {/* Modal de Validação de Números */}
      <ConfirmationModal
        isOpen={isValidationWarningModalOpen}
        onClose={() => setIsValidationWarningModalOpen(false)}
        onConfirm={handleConfirmValidationSend}
        title={t("Atenção: Números Não Verificados")}
        message={`${validationInfo.pendingCount + validationInfo.invalidCount} número(s) serão bloqueados:\n\n• ${validationInfo.pendingCount} Pendente(s)\n• ${validationInfo.invalidCount} Inválido(s)\n\nApenas ${validationInfo.validCount} número(s) validado(s) receberão mensagens.`}
        confirmText={t("Entendi, Enviar Apenas Validados")}
        cancelText={t("Cancelar")}
        tipMessage={t("Valide os números antes de enviar para evitar bloqueio do WhatsApp.")}
      />
    </>
  );
};

export default LeadsSalvos;